import {
  AttachEntitiesDto,
  CreatePresetDto,
  CreateStepDto,
  DisconnectPresetDto,
  PreviewPresetDto,
  PullPresetDto,
  RenamePresetDto,
  ReorderStepsDto,
  SetPresetDto,
  SetStatusDto,
  UpdatePresetDto,
  UpdateStepDto,
  UpdateSubtaskCategoryDto,
} from '@api/types';
import { QK } from '@api/queryKeys';
import { isAuthErrorHandledByInterceptor } from '@lib/api/serverStateErrorHandler';
import { statusKeys } from '@shared/api/query-keys';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { statusApi } from './status';
import { statusQueries } from './queries';
import { invalidateStatus, invalidateStatusPresets } from './invalidations';

// Query hooks
export const useStatus = (auditId?: string, enabled: boolean = true) =>
  useQuery({ ...statusQueries.overview(auditId), enabled: Boolean(auditId) && enabled });

export const useStatusTasks = (auditId?: string, enabled: boolean = true) =>
  useQuery({ ...statusQueries.tasks(auditId), enabled: Boolean(auditId) && enabled });

/**
 * The places that cite one task. Own root (`statusKeys.taskBacklinks`), so the board's
 * every-save invalidation of `statusKeys.all` leaves it alone. `enabled` is the
 * caller's role check: the route is auditor-only, and a client must not fire it.
 * A count above zero earns the citations counter and the disabled Delete; the save-time
 * refusal is still the authority (a client can be blocked by a citation it cannot see).
 */
export const useStatusTaskBacklinks = (
  auditId?: string,
  stepId?: string | number,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: statusKeys.taskBacklinks(auditId, stepId),
    queryFn: () => statusApi.getStepBacklinks(auditId as string, stepId as string | number),
    enabled: Boolean(auditId && stepId) && enabled,
  });

// Mutation hooks
export const usePreviewPreset = () => {
  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: PreviewPresetDto }) =>
      statusApi.previewPreset(auditId, payload),
  });
};

export const useStartEdit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (auditId: string) => statusApi.startEdit(auditId),
    onSuccess: (_, auditId) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

export const useSaveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (auditId: string) => statusApi.save(auditId),
    onSuccess: (_, auditId) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
  });
};

export const useReorderSteps = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: ReorderStepsDto }) =>
      statusApi.reorderSteps(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

export const useUpdatePreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: UpdatePresetDto }) =>
      statusApi.updatePreset(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

export const useSyncUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: UpdatePresetDto }) =>
      statusApi.syncUpdate(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
  });
};

export const usePullPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: PullPresetDto }) =>
      statusApi.pullPreset(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
  });
};

export const useDisconnectPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: DisconnectPresetDto }) =>
      statusApi.disconnect(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
  });
};

export const useCreateStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: CreateStepDto }) =>
      statusApi.createStep(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

/**
 * The drawer's content save (the findings picker). Off the audit team, the API refuses an audit
 * team's task with a non-auth 404; like an attach, the refusal is toasted and the board re-read,
 * whose refetch is the rollback of the edit the drawer already shows. A success re-reads nothing.
 */
export const useUpdateStep = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'status' });

  return useMutation({
    meta: { errorMessage: t('task_save_failed') },
    mutationFn: ({
      auditId,
      stepId,
      payload,
    }: {
      auditId: string;
      stepId: string | number;
      payload: UpdateStepDto;
    }) => statusApi.updateStep(auditId, stepId, payload),
    onError: (error, { auditId }) => {
      if (isAuthErrorHandledByInterceptor(error)) return;
      void invalidateStatus(queryClient, auditId);
    },
  });
};

/**
 * A step's attach re-sends the drawer's whole id list, so a board left stale by a colleague (a
 * swap deleted a document the step held) re-sends a dead id, and the API refuses the write with a
 * non-auth 404. The translated toast says so, and the board is re-read: the refetch replaces the
 * refused optimistic list in the board and the open drawer. That refetch is the rollback; a local
 * one would race it and could restore the dead id. A 401/403 is the interceptor's: it has logged
 * out already, so nothing is re-read. A success re-reads nothing: the optimistic write is what
 * the server stored.
 */
export const useAttachEntity = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'status' });

  return useMutation({
    meta: { errorMessage: t('attach_failed') },
    mutationFn: ({
      auditId,
      stepId,
      payload,
    }: {
      auditId: string;
      stepId: string | number;
      payload: AttachEntitiesDto;
    }) => statusApi.attachEntity(auditId, stepId, payload),
    onError: (error, { auditId }) => {
      if (isAuthErrorHandledByInterceptor(error)) return;
      void invalidateStatus(queryClient, auditId);
    },
  });
};

export const useDeleteStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, stepId }: { auditId: string; stepId: string | number }) =>
      statusApi.deleteStep(auditId, stepId),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

/**
 * A status change the API refuses — off the audit team, an audit team's task answers a non-auth
 * 404 — is toasted (the hook's `meta`) and the board re-read, which replaces the status the board
 * already shows. A 401/403 is the interceptor's: it has logged out, so nothing is re-read.
 */
const onRefusedStatusChange =
  (queryClient: QueryClient) =>
  (error: unknown, { auditId }: { auditId: string }) => {
    if (isAuthErrorHandledByInterceptor(error)) return;
    void invalidateStatus(queryClient, auditId);
  };

export const useMarkAsDone = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'status' });

  return useMutation({
    meta: { errorMessage: t('status_change_failed') },
    mutationFn: ({ auditId, stepId }: { auditId: string; stepId: string | number }) =>
      statusApi.markAsDone(auditId, stepId),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
    onError: onRefusedStatusChange(queryClient),
  });
};

export const useSetStatus = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'status' });

  return useMutation({
    meta: { errorMessage: t('status_change_failed') },
    mutationFn: ({
      auditId,
      stepId,
      payload,
    }: {
      auditId: string;
      stepId: string | number;
      payload: SetStatusDto;
    }) => statusApi.setStatus(auditId, stepId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
    onError: onRefusedStatusChange(queryClient),
  });
};

export const useToggleStatus = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dashboard', { keyPrefix: 'status' });

  return useMutation({
    meta: { errorMessage: t('status_change_failed') },
    mutationFn: ({ auditId, stepId }: { auditId: string; stepId: string | number }) =>
      statusApi.toggleStatus(auditId, stepId),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
    onError: onRefusedStatusChange(queryClient),
  });
};

export const useUpdateSubtaskCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      stepId,
      payload,
    }: {
      auditId: string;
      stepId: string | number;
      payload: UpdateSubtaskCategoryDto;
    }) => statusApi.updateSubtaskCategory(auditId, stepId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
    },
  });
};

export const useNewPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: CreatePresetDto }) =>
      statusApi.newPreset(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatusPresets(queryClient, auditId);
    },
  });
};

export const useSetPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: SetPresetDto }) =>
      statusApi.setPreset(auditId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatus(queryClient, auditId);
      queryClient.invalidateQueries({ queryKey: QK.audit(auditId) });
    },
  });
};

export const useRenamePreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      presetId,
      payload,
    }: {
      auditId: string;
      presetId: string | number;
      payload: RenamePresetDto;
    }) => statusApi.renamePreset(auditId, presetId, payload),
    onSuccess: (_, { auditId }) => {
      invalidateStatusPresets(queryClient, auditId);
    },
  });
};

export const useDeletePreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, presetId }: { auditId: string; presetId: string | number }) =>
      statusApi.deletePreset(auditId, presetId),
    onSuccess: (_, { auditId }) => {
      invalidateStatusPresets(queryClient, auditId);
    },
  });
};
